import PhotoSwipe from 'photoswipe';
import PhotoSwipeUIDefault from 'photoswipe/src/js/ui/photoswipe-ui-default';

export default class Gallery {
  constructor(elem) {
    this.gallerySelector = elem;

    this.galleryPrefix = 'gallery-';
    this.elements = `${this.galleryPrefix}elements`;
    this.uid = `${this.galleryPrefix}uid`;
    this.storage = sessionStorage;

    this.init();
  }

  init() {
    this._initSessionStorage();

    // select all galleries
    const galleries = document.querySelectorAll('[data-pswp-uid]');
    const uid = galleries.length
      ? this._getUID(galleries.length)
      : (this._setUID(), this._getUID());

    this._setUID(uid);
    this._setUIDAttr(uid);

    this.gallerySelector.addEventListener(
      'click',
      this._onThumbnailsClick.bind(this),
      false
    );

    // Parse URL and open gallery if it contains #&pid=3&gid=1
    const hashData = this._photoswipeParseHash();
    if (hashData.pid && hashData.gid) {
      const gallery = document.querySelector(
        `[data-pswp-uid="${hashData.gid}"]`
      );
      this._openPhotoSwipe(hashData.pid, gallery, true, true);
    }
  }

  // Creates the gallery keys in the session storage
  _initSessionStorage() {
    if (this.storage.getItem(this.uid) == null) {
      this.storage.setItem(this.uid, '0');
    }
  }

  _getUID(inc = 1) {
    return Number(this.storage.getItem(this.uid)) + inc;
  }

  _setUID(uid = 0) {
    this.storage.setItem(this.uid, uid.toString());
  }

  _setUIDAttr(uid) {
    this.gallerySelector.setAttribute('data-pswp-uid', uid.toString());
  }

  /* eslint-disable class-methods-use-this */
  _openPhotoSwipe(index, clickedGallery, disableAnimation, fromURL) {
    const pswpElement = document.querySelector('.pswp');

    const items = this._parseThumbnailElements(clickedGallery);

    // define options (if needed)
    const options = {
      galleryUID: clickedGallery.getAttribute('data-pswp-uid'),

      getThumbBoundsFn(idx) {
        const thumbnail = items[idx].el.children[0];
        const pageYScroll =
          window.pageYOffset || document.documentElement.scrollTop;
        const rect = thumbnail.getBoundingClientRect();

        return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
      },

      /* eslint-disable no-param-reassign */
      addCaptionHTMLFn(item, captionEl) {
        if (!item.title) {
          captionEl.children[0].innerText = '';

          return false;
        }

        captionEl.children[0].innerHTML = `${item.title}<br/><small>Photo: ${item.author}</small>`;

        return true;
      }
      /* eslint-enable no-param-reassign */
    };

    if (fromURL) {
      if (options.galleryPIDs) {
        // parse real index when custom PIDs are used
        // http://photoswipe.com/documentation/faq.html#custom-pid-in-url
        for (let j = 0; j < items.length; j += 1) {
          if (items[j].pid === index) {
            options.index = j;
            break;
          }
        }
      } else {
        options.index = parseInt(index, 10) - 1;
      }
    } else {
      options.index = parseInt(index, 10);
    }

    // exit if index not found
    if (Number.isNaN(options.index)) {
      return;
    }

    if (disableAnimation) {
      options.showAnimationDuration = 0;
    }

    // Pass data to PhotoSwipe and initialize it
    const gallery = new PhotoSwipe(
      pswpElement,
      PhotoSwipeUIDefault,
      items,
      options
    );

    // see: http://photoswipe.com/documentation/responsive-images.html
    let firstResize = true;
    let useLargeImages = false;
    let realViewportWidth;
    let imageSrcWillChange;

    gallery.listen('beforeResize', () => {
      let dpiRatio = window.devicePixelRatio ? window.devicePixelRatio : 1;
      dpiRatio = Math.min(dpiRatio, 2.5);
      realViewportWidth = gallery.viewportSize.x * dpiRatio;

      if (
        realViewportWidth >= 1200 ||
        (!gallery.likelyTouchDevice && realViewportWidth > 800) ||
        window.screen.width > 1200
      ) {
        if (!useLargeImages) {
          useLargeImages = true;
          imageSrcWillChange = true;
        }
      } else if (useLargeImages) {
        useLargeImages = false;
        imageSrcWillChange = true;
      }

      if (imageSrcWillChange && !firstResize) {
        gallery.invalidateCurrItems();
      }

      if (firstResize) {
        firstResize = false;
      }

      imageSrcWillChange = false;
    });

    gallery.listen('gettingData', (idx, item) => {
      /* eslint-disable no-param-reassign */
      if (useLargeImages) {
        item.src = item.o.src;
        item.w = item.o.w;
        item.h = item.o.h;
      } else {
        item.src = item.m.src;
        item.w = item.m.w;
        item.h = item.m.h;
      }
      /* eslint-enable no-param-reassign */
    });

    gallery.init();
  }

  _onThumbnailsClick(e) {
    e.preventDefault();

    const eTarget = e.target;
    const clickedListItem = eTarget.closest('a');

    if (clickedListItem) {
      const clickedGallery = clickedListItem.parentNode;
      const [...childNodes] = clickedGallery.childNodes;

      let nodeIndex = 0;
      let index;

      childNodes
        .filter(item => item.nodeType === 1)

        .forEach(item => {
          if (item === clickedListItem) {
            index = nodeIndex;
          } else {
            nodeIndex += 1;
          }
        });

      if (index >= 0) {
        this._openPhotoSwipe(index, clickedGallery);
      }
    }
  }

  _photoswipeParseHash() {
    const hash = window.location.hash.substring(1);
    const fragment = hash.charAt(0) === '&' ? hash.substr(1) : hash;
    const params = {};

    if (fragment.length >= 5) {
      // pid=1
      const items = fragment.split('&');

      items.forEach(item => {
        const [key, val] = item.split('=');

        params[key] = key === 'gid' ? parseInt(val, 10) : val;
      });
    }

    return params;
  }

  _parseThumbnailElements(elem) {
    const thumbElements = elem.childNodes;
    const numNodes = thumbElements.length;
    const items = [];

    for (let i = 0; i < numNodes; i += 1) {
      const el = thumbElements[i];

      // include only element nodes
      if (el.nodeType === 1) {
        const childElements = el.children;
        let size = el.getAttribute('data-size').split('x');

        // create slide object
        const item = {
          src: el.getAttribute('href'),

          w: parseInt(size[0], 10),

          h: parseInt(size[1], 10),

          author: el.getAttribute('data-author'),

          el // save link to element for getThumbBoundsFn
        };

        if (childElements.length > 0) {
          // thumbnail url
          item.msrc = childElements[0].getAttribute('src');
          if (childElements.length > 1) {
            // caption (contents of figure)
            item.title = childElements[1].innerHTML;
          }
        }

        const mediumSrc = el.getAttribute('data-med');
        if (mediumSrc) {
          size = el.getAttribute('data-med-size').split('x');

          // "medium-sized" image
          item.m = {
            src: mediumSrc,

            w: parseInt(size[0], 10),

            h: parseInt(size[1], 10)
          };
        }

        // original image
        item.o = {
          src: item.src,

          w: item.w,

          h: item.h
        };

        items.push(item);
      }
    }

    return items;
  }
  /* eslint-enable class-methods-use-this */
}



// WEBPACK FOOTER //
// ./src/main/javascripts/modules/gallery.js